import React, { useCallback, useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import deLocale from '@fullcalendar/core/locales/de';
import plLocale from '@fullcalendar/core/locales/pl';
import type { DateSelectArg, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Button, Drawer } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, getThemeColors, DENSITY_VALUES } from '../../lib/theme';
import { toLocalDateKey, toLocalDatetimeInput } from '../../lib/localDate';
import { formatTime } from '../../lib/dateFormat';
import { v2CalendarCategories, v2CalendarEvents } from '../../lib/calendarEvents';
import type { CalendarCategory, CalendarEvent } from '../../lib/calendarEvents';
import { msgraphApi } from '../../lib/msgraph';
import { navigateGlobal, buildPath } from '../../lib/route';
import { rowLinkProps } from '../../lib/openLink';
import type { Density } from '../../types';
import { DateTimeLocalInput } from '../../components/DateTimeInput';

interface KalenderPageProps {
  dark: boolean;
  density: Density;
}

type FCView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

interface SidebarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date | null;
  color: string;
  allDay: boolean;
  description?: string | null;
  source: 'custom' | 'care' | 'outlook';
  categoryId?: number | null;
  /** Exchange-Sync: letzter Fehler beim Spiegeln nach Outlook. */
  syncError?: string | null;
  /** Paket F1 — PB-Bezug: erlaubt „Profil öffnen" aus dem Termin heraus. */
  userId?: number | null;
  userName?: string | null;
  /** 1 = BK (→ /betreuung/{id}), sonst PB (→ /pflege/{id}). */
  userRole?: number | null;
  accountId?: number | null;
  profileId?: number | null;
}

/** Farbe der aus Outlook gelesenen Termine — bewusst anders als CRM-Termine. */
const OUTLOOK_COLOR = '#0f6cbd';

/** Schalter „Outlook-Termine anzeigen" — reine Ansichtssache, daher pro Browser. */
const OUTLOOK_PREF_KEY = 'sh24.kalender.showOutlook';

function readOutlookPref(): boolean {
  try {
    return window.localStorage.getItem(OUTLOOK_PREF_KEY) !== '0';
  } catch {
    return true;
  }
}

interface NewEventForm {
  title: string;
  description: string;
  start_at: string;
  end_at: string;
  all_day: boolean;
  category_id: string;
}

const DEFAULT_FORM: NewEventForm = {
  title: '',
  description: '',
  start_at: '',
  end_at: '',
  all_day: false,
  category_id: '',
};

function toDatetimeLocal(iso: string): string {
  if (!iso) return '';
  // Convert ISO string to datetime-local value (YYYY-MM-DDTHH:mm).
  // Ganztagstermine liefert FullCalendar als reines Datum ("2026-12-24") —
  // ein <input type="datetime-local"> verwirft so einen Wert und bleibt LEER,
  // das Speichern scheiterte danach an der Pflichtfeld-Prüfung. Deshalb auf
  // Mitternacht ergänzen.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return `${iso}T00:00`;
  return iso.slice(0, 16);
}

/**
 * `Date` → datetime-local in LOKALER Zeit.
 *
 * `toISOString()` liefert UTC — aus 09:00 wurde im Formular 07:00, und seit
 * dem Exchange-Sync landet diese verschobene Zeit auch im Outlook-Kalender.
 */
function dateToDatetimeLocal(date: Date): string {
  return toLocalDatetimeInput(date);
}

/**
 * Ganztags-Ende umrechnen.
 *
 * Die API führt bei Ganztagsterminen den LETZTEN TAG als `end_at`
 * (24.–26.12. endet am 26.12.). FullCalendar erwartet das Ende dagegen
 * EXKLUSIV, also den Folgetag — ohne diese Umrechnung fehlt in der Anzeige
 * jeweils der letzte Tag, und beim Speichern wandert er dauerhaft weg.
 */
function shiftDay(value: string | null | undefined, days: number): string | undefined {
  if (!value) return undefined;
  // Ein reines Datum ("2026-09-24") parst `new Date()` als UTC-Mitternacht —
  // in Berlin wäre das 02:00 und der verschobene Tag stünde mit Uhrzeit da.
  // Deshalb Datumsangaben explizit als lokale Mitternacht aufbauen.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  d.setDate(d.getDate() + days);
  return dateToDatetimeLocal(d) + ':00';
}

function fromDatetimeLocal(val: string): string {
  if (!val) return '';
  return val + ':00';
}

export function KalenderPage({ dark, density }: KalenderPageProps) {
  const t = getThemeColors(dark);
  const { t: tr, i18n } = useTranslation();
  const d = DENSITY_VALUES[density];
  // FullCalendar-Locale und Date/Time-Formatierung folgen der UI-Sprache statt
  // fest 'de' — sonst bleibt der Kalender auch unter EN/PL deutsch.
  const fcLocale = i18n.language === 'pl' ? plLocale : i18n.language === 'en' ? 'en' : deLocale;
  const dateLocaleTag = i18n.language === 'pl' ? 'pl-PL' : i18n.language === 'en' ? 'en-US' : 'de-DE';
  const calRef = useRef<FullCalendar>(null);

  const [view, setView] = useState<FCView>('dayGridMonth');
  const [fcEvents, setFcEvents] = useState<EventInput[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [sidebarEvents, setSidebarEvents] = useState<SidebarEvent[]>([]);
  const [categories, setCategories] = useState<CalendarCategory[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<NewEventForm>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Inject FullCalendar CSS overrides that match the theme
  useEffect(() => {
    const styleId = 'fc-theme-override';
    let el = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      document.head.appendChild(el);
    }
    el.textContent = `
      .fc-theme-standard td, .fc-theme-standard th {
        border-color: ${t.border} !important;
      }
      .fc td, .fc th {
        background: ${t.card} !important;
      }
      .fc-col-header-cell-cushion {
        color: ${t.muted} !important;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        text-decoration: none !important;
      }
      .fc-daygrid-day-number {
        color: ${t.heading} !important;
        text-decoration: none !important;
        font-size: 12px;
        font-weight: 500;
      }
      .fc-day-today {
        background: ${BRAND}15 !important;
      }
      .fc-day-today .fc-daygrid-day-number {
        background: ${BRAND};
        color: #fff !important;
        border-radius: 50%;
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
      }
      .fc-event {
        border-radius: 5px !important;
        font-size: 11px !important;
        border: none !important;
        cursor: pointer;
      }
      .fc-event-title {
        font-weight: 500;
      }
      .fc-toolbar-title {
        color: ${t.heading} !important;
        font-size: 18px !important;
        font-weight: 700 !important;
        font-family: Outfit, sans-serif;
      }
      .fc-button, .fc-button-primary {
        display: none !important;
      }
      .fc-scrollgrid {
        border-color: ${t.border} !important;
      }
      .fc-timegrid-slot {
        border-color: ${t.borderSoft} !important;
      }
      .fc-timegrid-axis {
        border-color: ${t.border} !important;
      }
      .fc-timegrid-slot-label-cushion {
        color: ${t.muted} !important;
        font-size: 11px;
      }
      .fc-daygrid-day.fc-day-selected {
        background: ${BRAND}18 !important;
        outline: 1px solid ${BRAND};
      }
      .fc-more-link {
        color: ${BRAND} !important;
        font-size: 10px;
      }
    `;
    return () => {
      el?.remove();
    };
  }, [t, dark]);

  // Nur eigene Termine? Seit Stage-Aktionen Termine für die zuständige Person
  // anlegen, füllt sich der geteilte Kalender mit fremden Einträgen.
  const [onlyMine, setOnlyMine] = useState(false);
  // Outlook-Termine (Exchange-Sync, Leserichtung). `connected` steuert nur den
  // Hinweistext — ohne verbundenes Konto bleibt der Kalender unverändert.
  const [showOutlook, setShowOutlook] = useState(readOutlookPref);
  const [outlookState, setOutlookState] = useState<{ connected: boolean; calendarEnabled: boolean; truncated: boolean }>(
    { connected: false, calendarEnabled: false, truncated: false },
  );
  const lastRangeRef = useRef<{ start: string; end: string } | null>(null);

  // Load categories once
  useEffect(() => {
    v2CalendarCategories.list().then(setCategories).catch(() => {});
  }, []);

  // Load events when calendar date range changes
  const handleDatesSet = useCallback(async (info: DatesSetArg) => {
    const start = info.startStr;
    const end = info.endStr;
    const allEvents: EventInput[] = [];

    // 1. Custom events from v2
    try {
      const customs: CalendarEvent[] = await v2CalendarEvents.list(start, end, onlyMine);
      for (const ev of customs) {
        allEvents.push({
          id: `custom-${ev.id}`,
          // Besitzer im Titel, sonst ist im geteilten Kalender nicht zu sehen,
          // in wessen Kalender ein automatisch angelegter Termin gehört.
          title: ev.admin_name ? `${ev.title} · ${ev.admin_name}` : ev.title,
          start: ev.start_at,
          end: ev.all_day ? shiftDay(ev.end_at ?? ev.start_at, 1) : (ev.end_at ?? undefined),
          allDay: ev.all_day,
          backgroundColor: ev.category?.color ?? BRAND,
          extendedProps: {
            source: 'custom', description: ev.description, apiId: ev.id,
            categoryId: ev.category?.id ?? null,
            adminId: ev.admin_id, adminName: ev.admin_name,
            syncError: ev.ms_sync_error ?? null,
            userId: ev.user_id ?? null,
            userName: ev.user_name ?? null,
            userRole: ev.user_role ?? null,
            accountId: ev.account_id ?? null,
            profileId: ev.profile_id ?? null,
          },
        });
      }
    } catch {
      // ignore — API may not be wired yet
    }

    // 2. Outlook-Termine des eingeloggten Admins (read-only).
    //    Eigener try/catch: eine fehlende oder kaputte Microsoft-Verbindung
    //    darf den CRM-Kalender nicht mitreißen.
    if (showOutlook) {
      try {
        const res = await msgraphApi.calendar(start, end);
        setOutlookState({ connected: res.connected, calendarEnabled: res.calendarEnabled, truncated: res.truncated });
        for (const ev of res.events) {
          if (!ev.start_at) continue;
          allEvents.push({
            id: `outlook-${ev.id}`,
            title: ev.is_cancelled ? `${ev.title} (abgesagt)` : ev.title,
            start: ev.start_at,
            end: ev.all_day ? shiftDay(ev.end_at ?? ev.start_at, 1) : (ev.end_at ?? undefined),
            allDay: ev.all_day,
            backgroundColor: ev.is_cancelled ? t.muted : OUTLOOK_COLOR,
            extendedProps: {
              source: 'outlook',
              description: ev.description,
              location: ev.location,
              organizer: ev.organizer,
              webLink: ev.web_link,
              cancelled: ev.is_cancelled,
            },
          });
        }
      } catch (e) {
        // 409 = verbunden, aber Microsoft verweigert den Zugriff (Consent weg
        // oder nie erteilt). Das ist kein „nicht verbunden" — der Hinweis zum
        // Neu-Verbinden soll erscheinen, statt dass der Schalter still wird.
        const status = (e as { response?: { status?: number } })?.response?.status;
        setOutlookState({
          connected: status === 409,
          calendarEnabled: false,
          truncated: false,
        });
      }
    }

    setFcEvents(allEvents);
    lastRangeRef.current = { start, end };
  }, [onlyMine, showOutlook]);

  // Filterwechsel lädt den aktuell sichtbaren Zeitraum erneut.
  useEffect(() => {
    const range = lastRangeRef.current;
    if (!range) return;
    void handleDatesSet({ startStr: range.start, endStr: range.end } as DatesSetArg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyMine, showOutlook]);

  const openNew = (date?: Date) => {
    setEditingId(null);
    // Lokale Zeit, nicht toISOString(): nach Klick auf den 25.09. stand sonst
    // „24.09. 22:00" (UTC) im Formular.
    const iso = dateToDatetimeLocal(date ?? selectedDate);
    setForm({ ...DEFAULT_FORM, start_at: iso, end_at: iso });
    setDrawerOpen(true);
  };

  const openEdit = (ev: SidebarEvent) => {
    if (ev.source !== 'custom') return;
    const apiId = Number(ev.id.replace('custom-', ''));
    setEditingId(apiId);
    setForm({
      title: ev.title,
      description: ev.description ?? '',
      // Lokale Zeit, nicht toISOString() — sonst steht im Formular (und nach
      // dem Speichern in Outlook) die UTC-Zeit.
      start_at: dateToDatetimeLocal(ev.start),
      // Ganztags: FullCalendars Ende ist exklusiv, die API will den letzten Tag.
      end_at: ev.end
        ? (ev.allDay ? toDatetimeLocal(shiftDay(dateToDatetimeLocal(ev.end), -1) ?? '') : dateToDatetimeLocal(ev.end))
        : '',
      all_day: ev.allDay,
      category_id: ev.categoryId ? String(ev.categoryId) : '',
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => { setDrawerOpen(false); setEditingId(null); setForm(DEFAULT_FORM); };

  /**
   * Paket F1 — Termine mit PB-Bezug (Wiedervorlage/Anamnese) verlinken auf
   * das Profil. `navigateGlobal` statt eines `navigate`-Props, weil
   * KalenderPage (wie CaregiverDetail) ohne Routing-Prop gerendert wird —
   * siehe die Doku dazu in `lib/route.ts`.
   */
  const profilePath = (ev: SidebarEvent) =>
    ev.userId == null ? null : buildPath(ev.userRole === 1 ? 'betreuung' : 'pflege', String(ev.userId));

  const openProfile = (ev: SidebarEvent) => {
    if (ev.userId == null) return;
    // BK-Wiedervorlage (user_role=1) → BK-Detailseite statt PB-Profil.
    navigateGlobal(ev.userRole === 1 ? 'betreuung' : 'pflege', String(ev.userId));
  };

  const refreshEvents = () => {
    const api = calRef.current?.getApi();
    if (api) {
      handleDatesSet({ startStr: api.view.activeStart.toISOString(), endStr: api.view.activeEnd.toISOString() } as DatesSetArg);
    }
  };

  // When a day cell is clicked, show sidebar for that date
  const handleDateClick = useCallback((info: { date: Date; allDay: boolean }) => {
    setSelectedDate(info.date);
  }, []);

  // When an event is clicked, jump to that day and open edit if custom
  const handleEventClick = useCallback((info: EventClickArg) => {
    const start = info.event.start;
    if (start) setSelectedDate(start);
    const source = info.event.extendedProps?.source as string | undefined;
    if (source === 'custom') {
      const apiId = info.event.extendedProps?.apiId as number | undefined;
      if (apiId) {
        const catId = info.event.extendedProps?.categoryId as number | null | undefined;
        setEditingId(apiId);
        setForm({
          title: info.event.title,
          description: String(info.event.extendedProps?.description ?? ''),
          start_at: toDatetimeLocal(info.event.startStr),
          // Ganztags: FullCalendars Ende ist exklusiv, die API will den letzten Tag.
          end_at: info.event.end
            ? (info.event.allDay
                ? toDatetimeLocal(shiftDay(info.event.endStr, -1) ?? '')
                : toDatetimeLocal(info.event.endStr))
            : '',
          all_day: info.event.allDay,
          category_id: catId ? String(catId) : '',
        });
        setDrawerOpen(true);
      }
    }
  }, []);

  // Keep sidebar events in sync with selectedDate + fcEvents
  useEffect(() => {
    // Tagesschlüssel in LOKALER Zeit auf beiden Seiten. Über toISOString()
    // war die lokale Mitternacht des 25.09. der 24.09. (UTC) — der ganztägige
    // Anamnese-Termin vom 25.09. erschien erst beim Klick auf den 26.09.
    const dayStr = toLocalDateKey(selectedDate);
    const matching: SidebarEvent[] = fcEvents
      .filter(ev => toLocalDateKey(ev.start as Date | string | undefined) === dayStr)
      .map(ev => ({
        id: String(ev.id ?? ''),
        title: String(ev.title ?? ''),
        start: new Date(ev.start as string),
        end: ev.end ? new Date(ev.end as string) : null,
        color: String(ev.backgroundColor ?? BRAND),
        allDay: Boolean(ev.allDay),
        description: String((ev.extendedProps as Record<string, unknown>)?.description ?? '') || null,
        source: ((ev.extendedProps as Record<string, unknown>)?.source as 'custom' | 'care' | 'outlook') ?? 'custom',
        categoryId: ((ev.extendedProps as Record<string, unknown>)?.categoryId as number | null) ?? null,
        syncError: ((ev.extendedProps as Record<string, unknown>)?.syncError as string | null) ?? null,
        userId: ((ev.extendedProps as Record<string, unknown>)?.userId as number | null) ?? null,
        userName: ((ev.extendedProps as Record<string, unknown>)?.userName as string | null) ?? null,
        userRole: ((ev.extendedProps as Record<string, unknown>)?.userRole as number | null) ?? null,
        accountId: ((ev.extendedProps as Record<string, unknown>)?.accountId as number | null) ?? null,
        profileId: ((ev.extendedProps as Record<string, unknown>)?.profileId as number | null) ?? null,
      }));
    setSidebarEvents(matching);
  }, [selectedDate, fcEvents]);

  // Switch calendar view
  const switchView = (v: FCView) => {
    setView(v);
    calRef.current?.getApi().changeView(v);
  };

  const goToday = () => {
    setSelectedDate(new Date());
    calRef.current?.getApi().today();
  };

  const handleSave = async () => {
    if (!form.title || !form.start_at) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        start_at: fromDatetimeLocal(form.start_at),
        end_at: form.end_at ? fromDatetimeLocal(form.end_at) : null,
        all_day: form.all_day,
        category_id: form.category_id ? Number(form.category_id) : null,
      };
      if (editingId) {
        await v2CalendarEvents.update(editingId, payload);
      } else {
        await v2CalendarEvents.create(payload);
      }
      closeDrawer();
      refreshEvents();
    } catch {
      // ignore for now
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    setDeleting(true);
    try {
      await v2CalendarEvents.remove(editingId);
      closeDrawer();
      refreshEvents();
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  const dateLabel = selectedDate.toLocaleDateString(dateLocaleTag, { weekday: 'long', day: 'numeric', month: 'long' });
  const [weekday, ...rest] = dateLabel.split(',');

  const viewLabels: Record<FCView, string> = {
    dayGridMonth: tr('kalender.view.month'),
    timeGridWeek: tr('kalender.view.week'),
    timeGridDay: tr('kalender.view.day'),
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: d.gap, height: '100%', minHeight: 0 }}>
      {/* ── Calendar ── */}
      <Card dark={dark} style={{ padding: d.cardPad, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {/* Custom toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => calRef.current?.getApi().prev()}
              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${t.border}`, background: t.card, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icon.ChevronLR dir="left" c={t.muted} />
            </button>
            <button
              onClick={() => calRef.current?.getApi().next()}
              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${t.border}`, background: t.card, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icon.ChevronLR dir="right" c={t.muted} />
            </button>
            <Button variant="secondary" size="sm" dark={dark} onClick={goToday}>
              {tr('kalender.action.today')}
            </Button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5,
                color: t.muted, fontFamily: 'Outfit, sans-serif', cursor: 'pointer',
                border: `1px solid ${t.border}`, borderRadius: 9, padding: '0 10px',
                background: onlyMine ? t.card : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={onlyMine}
                onChange={e => setOnlyMine(e.target.checked)}
                aria-label={tr('kalender.filter.only_mine')}
              />
              {tr('kalender.filter.only_mine')}
            </label>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: t.muted,
                border: `1px solid ${t.border}`, borderRadius: 9, padding: '5px 9px',
                background: showOutlook ? t.card : 'transparent', cursor: 'pointer',
              }}
              title={
                outlookState.connected && !outlookState.calendarEnabled
                  ? tr('kalender.filter.outlook_hint_disconnected')
                  : tr('kalender.filter.outlook_hint_connected')
              }
            >
              <input
                type="checkbox"
                checked={showOutlook}
                onChange={e => {
                  setShowOutlook(e.target.checked);
                  try { window.localStorage.setItem(OUTLOOK_PREF_KEY, e.target.checked ? '1' : '0'); } catch { /* egal */ }
                }}
                aria-label={tr('kalender.filter.outlook_show')}
              />
              Outlook
              {showOutlook && outlookState.connected && !outlookState.calendarEnabled && (
                <span style={{ color: '#b54708' }} title={tr('kalender.filter.outlook_missing_share')}>!</span>
              )}
              {/* Graph liefert seitenweise; bei sehr vollen Kalendern kann der
                  Rest abgeschnitten sein — das darf nicht still passieren. */}
              {showOutlook && outlookState.truncated && (
                <span style={{ color: '#b54708' }} title={tr('kalender.filter.outlook_truncated')}>…</span>
              )}
            </label>
            <div style={{ display: 'flex', background: t.cardMuted, border: `1px solid ${t.border}`, borderRadius: 9, padding: 2 }}>
              {(['dayGridMonth', 'timeGridWeek', 'timeGridDay'] as FCView[]).map(v => (
                <button
                  key={v}
                  onClick={() => switchView(v)}
                  style={{
                    padding: '5px 11px', borderRadius: 7,
                    background: view === v ? t.card : 'transparent',
                    color: view === v ? BRAND : t.muted,
                    border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 500,
                    fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  {viewLabels[v]}
                </button>
              ))}
            </div>
            <Button
              variant="primary"
              size="sm"
              dark={dark}
              icon={<Icon.Plus />}
              onClick={() => openNew()}
            >
              {tr('kalender.action.new_appointment')}
            </Button>
          </div>
        </div>

        {/* FullCalendar */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <FullCalendar
            ref={calRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale={fcLocale}
            headerToolbar={false}
            events={fcEvents}
            datesSet={handleDatesSet}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            selectable={true}
            select={(info: DateSelectArg) => {
              setSelectedDate(info.start);
              openNew(info.start);
            }}
            height="100%"
            firstDay={1}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', meridiem: false }}
          />
        </div>
      </Card>

      {/* ── Day detail sidebar ── */}
      <Card dark={dark} style={{ padding: d.cardPad, display: 'flex', flexDirection: 'column' }}>
        <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${t.borderSoft}` }}>
          <div style={{ fontSize: 11, color: t.muted, fontWeight: 600, textTransform: 'uppercase' }}>
            {weekday}
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: t.heading, marginTop: 2 }}>
            {rest.join(',').trim()}
          </div>
          <div style={{ fontSize: 12, color: t.muted, marginTop: 4 }}>
            {tr('kalender.events', { count: sidebarEvents.length })}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1 }}>
          {sidebarEvents.length === 0 ? (
            <div style={{ textAlign: 'center', color: t.muted, padding: 28, fontSize: 13 }}>
              {tr('kalender.action.no_events')}
            </div>
          ) : sidebarEvents.map((ev, i) => (
            <div
              key={ev.id || i}
              style={{
                background: t.cardMuted,
                borderLeft: `3px solid ${ev.color}`,
                borderRadius: 8, padding: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
                <Badge
                  tone={ev.source === 'care' ? 'success' : ev.source === 'outlook' ? 'info' : 'brand'}
                  dark={dark}
                >
                  {ev.source === 'care' ? tr('pflege.detail.section.care') : ev.source === 'outlook' ? 'Outlook' : tr('kalender.tag.termin')}
                </Badge>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {!ev.allDay && ev.start && (
                    <div style={{ fontSize: 11, color: t.muted }}>
                      {formatTime(ev.start)}
                      {ev.end && ` – ${formatTime(ev.end)}`}
                    </div>
                  )}
                  {ev.source === 'custom' && (
                    <button
                      onClick={() => openEdit(ev)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: t.muted, display: 'flex' }}
                      title={tr('common.action.edit')}
                    >
                      <Icon.Edit c={t.muted} size={13} />
                    </button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{ev.title}</div>
              {ev.description && (
                <div style={{ fontSize: 12, color: t.muted, marginTop: 3 }}>{ev.description}</div>
              )}
              {/* Paket F1 — Termine mit PB-Bezug (Wiedervorlage/Anamnese) springen
                  direkt ins Profil, statt dass man den Namen erst suchen muss. */}
              {ev.accountId != null && ev.profileId != null && (
                <button
                  {...rowLinkProps(profilePath(ev), () => openProfile(ev))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: 0, marginTop: 6, fontSize: 11.5, fontWeight: 600,
                    color: BRAND, fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  {ev.userName ? tr('kalender.sidebar.open_profile_named', { name: ev.userName }) : tr('call_popup.open_profile')}
                  <Icon.ChevronLR dir="right" c={BRAND} />
                </button>
              )}
              {/* Ein gescheiterter Spiegel war bisher unsichtbar: der Termin
                  wurde gespeichert, landete nie in Outlook, niemand erfuhr es. */}
              {ev.syncError && (
                <div style={{ fontSize: 11, color: '#b54708', marginTop: 4 }}>
                  {tr('kalender.sidebar.sync_error', { error: ev.syncError })}
                </div>
              )}
            </div>
          ))}
        </div>

        <Button
          variant="secondary"
          dark={dark}
          icon={<Icon.Plus c={BRAND} />}
          size="sm"
          style={{ marginTop: 12, justifyContent: 'center', color: BRAND }}
          onClick={() => openNew()}
        >
          {tr('kalender.action.new_full')}
        </Button>
      </Card>

      {/* ── Event Drawer ── */}
      <Drawer open={drawerOpen} onClose={closeDrawer} width={480} dark={dark}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Drawer header */}
          <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${t.borderSoft}`, flexShrink: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.heading }}>
              {editingId ? tr('pflege.zeiterfassung.drawer.editTitle') : tr('kalender.action.new_appointment')}
            </div>
          </div>

          {/* Drawer body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 5, textTransform: 'uppercase' }}>{tr('kalender.form.title')} *</div>
              <input
                placeholder={tr('kalender.form.title_placeholder')}
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                style={inputStyle(t)}
                autoFocus
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 5, textTransform: 'uppercase' }}>{tr('kalender.form.start')}</div>
                <DateTimeLocalInput
                  ariaLabel={tr('kalender.form.start')}
                  value={form.start_at}
                  onChange={v => setForm(f => ({ ...f, start_at: v }))}
                  style={inputStyle(t)}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 5, textTransform: 'uppercase' }}>{tr('kalender.form.end')}</div>
                <DateTimeLocalInput
                  ariaLabel={tr('kalender.form.end')}
                  value={form.end_at}
                  onChange={v => setForm(f => ({ ...f, end_at: v }))}
                  style={inputStyle(t)}
                />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 5, textTransform: 'uppercase' }}>{tr('kalender.form.category_label')}</div>
              <select
                value={form.category_id}
                onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                style={{ ...inputStyle(t), color: form.category_id ? t.heading : t.muted }}
              >
                <option value="">{tr('kalender.form.category_placeholder')}</option>
                {categories.map(c => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 5, textTransform: 'uppercase' }}>{tr('kalender.form.description')}</div>
              <textarea
                placeholder={tr('settings.bk_stages.field.description_placeholder')}
                value={form.description}
                rows={4}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                style={{ ...inputStyle(t), resize: 'vertical', minHeight: 80 }}
              />
            </div>
          </div>

          {/* Drawer footer */}
          <div style={{ padding: '14px 24px', borderTop: `1px solid ${t.borderSoft}`, display: 'flex', gap: 8, justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              {editingId && (
                <Button variant="secondary" size="sm" dark={dark} onClick={handleDelete} disabled={deleting} style={{ color: '#f04438' }}>
                  {deleting ? '…' : tr('common.action.delete')}
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" size="sm" dark={dark} onClick={closeDrawer}>
                {tr('common.cancel')}
              </Button>
              <Button variant="primary" size="sm" dark={dark} onClick={handleSave} disabled={saving || !form.title}>
                {saving ? '…' : tr('common.save')}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
}

function inputStyle(t: ReturnType<typeof getThemeColors>): React.CSSProperties {
  return {
    width: '100%',
    padding: '7px 10px',
    borderRadius: 7,
    border: `1px solid ${t.border}`,
    background: t.card,
    color: t.heading,
    fontSize: 13,
    fontFamily: 'Outfit, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
  };
}
